import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { IWCCStorageManager } from 'managers/iStorage';
import { EffectsContainer } from 'mixins/withEffects';
import WCCAttachment from 'models/attachments/attachment';
import { CardsScoreAnswer } from 'models/cardsScoreAnswer';
import { IWCCContentBlock } from './blocks/block/block.interfaces';
import './content.html';
import './content.scss';
import WCCContentDocument from './document';
import WCCContentParser from './parser';
import { IWCCContentParser } from './parser.interfaces';
import AsciiEmojiRule from './rules/asciiEmoji';
import AttachmentsRule from './rules/attachments';
import VideosRule from './rules/videos';
import WCCContentRule from './rules/base';
import CardsScoreRule from './rules/cardsScoreAnswer';
import EmojiRule from './rules/emoji';
import LinkRule from './rules/link';
import defaultRules from './rules/sets/default';
import noMediaRules from './rules/sets/nomedia';
import forHighlightRules from './rules/sets/highlights';
import WCCContentNewLineReplacementRule from "./rules/newLineReplacement";

components
    .registerLocal('wcc-content-attachments', () => require('./components/attachments/attachments'))
    .registerLocal('wcc-content-cards-score-answer', () => require('./components/cardsScoreAnswer/cardsScoreAnswer'));

function comparator(oldBlock: IWCCContentBlock, newBlock: IWCCContentBlock) {
    return oldBlock.equals(newBlock);
}

export interface WCCContentVMConfig {
    topicId: SubscribableOrNullableValue<string>
    threadId: SubscribableOrNullableValue<string | undefined>
    content: SubscribableOrNullableValue<string>
    maxLength: SubscribableOrNullableValue<number>

    emojis: SubscribableOrNullableValue<boolean>
    asciiEmojis: SubscribableOrNullableValue<boolean>
    links: SubscribableOrNullableValue<boolean>
    noMedia: SubscribableOrNullableValue<boolean>
    parseNewLines: SubscribableOrNullableValue<boolean>
    forHighlights: SubscribableOrNullableValue<boolean>

    attachments: SubscribableOrNullableValue<Array<WCCAttachment>>
    cardsScoreAnswers: SubscribableOrNullableValue<Array<CardsScoreAnswer>>
}

@injectable()
export default class WCCContentVM {
    private parser: Subscribable<IWCCContentParser>

    private allowEmojis: Subscribable<boolean>
    private allowAsciiEmojis: Subscribable<boolean>
    private allowLinks: Subscribable<boolean>
    private forbidMedia: Subscribable<boolean>
    private forHighlights: Subscribable<boolean>
    private selectedRulesOnlyMode: Subscribable<boolean>
    private parseNewLines: Subscribable<boolean>

    private topicId: Subscribable<string | undefined>

    private content: Subscribable<string>

    private attachments: Subscribable<Array<WCCAttachment>>
    private cardsScoreAnswers: Subscribable<Array<CardsScoreAnswer>>

    nodes: Subscribable<Node[]>

    constructor(
        @inject(wccModules.componentConfig) config: WCCContentVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const maxLength = ko.flattenComputed(config.maxLength, 0);

        this.allowEmojis = ko.flattenComputed(config.emojis, false);
        this.allowAsciiEmojis = ko.flattenComputed(config.asciiEmojis, this.allowEmojis);
        this.allowLinks = ko.flattenComputed(config.links, false);
        this.forbidMedia = ko.flattenComputed(config.noMedia, false);
        this.forHighlights = ko.flattenComputed(config.forHighlights, false);
        this.parseNewLines = ko.flattenComputed(config.parseNewLines, false);

        this.topicId = ko.flattenComputed(config.topicId);
        this.content = ko.flattenComputed(config.content, '');

        this.attachments = ko.flattenComputed(config.attachments, []);
        this.cardsScoreAnswers = ko.flattenComputed(config.cardsScoreAnswers, []);

        this.selectedRulesOnlyMode = ko.pureComputed(() => this.allowEmojis() || this.allowLinks());

        this.parser = ko.pureComputed(() => this.getParser());

        const document = this.parser.pluck(parser => this.getParsedDocument(parser));
        const limitedDocument = ko.pureComputed(() => document().take(maxLength()));
        const blocks = limitedDocument.pluck(d => d.blocks).cache(comparator);
        this.nodes = blocks.pluck(block => block.nodes).flatten();

        effects.register(parser => {
            if (parser != undefined)
                return () => parser.dispose();
        }, [this.parser]);


        const bindings = {
            'widgetsContainer': true,
            'css': { 'wcc-content': true, 'wcc-content-highlightable': this.forHighlights },
            'attr': <StringMap<any>>{ }
        }

        if (config.threadId) {
            bindings.attr['data-thread-id'] = config.threadId;
        }

        ko.applyBindingsToNode(info.element, bindings);
    }

    private getParser(): IWCCContentParser {
        let rules = new Array<WCCContentRule>();

        if (this.cardsScoreAnswers().length > 0)
            rules.push(new CardsScoreRule(this.cardsScoreAnswers));

        if (this.attachments().length > 0) {
            rules.push(new AttachmentsRule(this.attachments));
            rules.push(new VideosRule(this.attachments));
        }

        if(this.forHighlights()) {
            rules = [...rules, ...forHighlightRules];
        }
        else {
            if (this.selectedRulesOnlyMode()) {
                if (this.allowEmojis()) {
                    rules.push(new EmojiRule());

                    if (this.allowAsciiEmojis())
                        rules.push(new AsciiEmojiRule());
                }

                if (this.allowLinks())
                    rules.push(new LinkRule());
            } else if (this.forbidMedia()) {
                rules = [...rules, ...noMediaRules];
            } else {
                rules = [...rules, ...defaultRules];
            }

            if (this.parseNewLines()) {
                rules.push(new WCCContentNewLineReplacementRule());
            }
        }

        return new WCCContentParser(rules);
    }

    private getParsedDocument(parser: IWCCContentParser): WCCContentDocument {
        return parser.parse(this.content(), { topicId: this.topicId });
    }
}